/**
 * Quando o cartão de conquista pode aparecer.
 *
 * Três condições, todas obrigatórias:
 *
 *  1. **Rota segura.** Allowlist, não denylist: tela nova nasce SEM celebração
 *     até alguém decidir que ela é segura. O contrário — esquecer de excluir
 *     `/pro/sucesso` — coloca um cartão em cima de um checkout recém-pago.
 *  2. **Nenhum bloqueio ativo** (`celebrationGate`): Foco, conclusão de livro,
 *     Studio aberto, promoção de liga.
 *  3. **Assentamento de 600ms.** A tela precisa existir antes da celebração.
 *     Sem isso o cartão nasce junto com o esqueleto da Home, sobre conteúdo
 *     que ainda está pipocando.
 *
 * A fila persiste (localStorage), então nada disso perde conquista — só adia.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  dequeueBadges,
  subscribeBadgeQueue,
} from "../utils/badgeUnlockQueue";
import { isCelebrationBlocked, subscribeCelebrationGate } from "../utils/celebrationGate";

/** ~600ms: o tempo de a tela assentar, do mock. */
const SETTLE_MS = 600;

/**
 * Telas onde celebrar é bem-vindo. Prefixos, para cobrir `/livro/:slug`,
 * `/leagues/:roomId` e afins.
 *
 * Fora daqui de propósito: `/onboarding` e `/pro*` (fluxos críticos),
 * `/search`, `/discover`, `/ai` (o usuário está no meio de uma tarefa),
 * `/insights` (tela de leitura contínua), `/settings`, `/admin`.
 */
const SAFE_ROUTE_PREFIXES = [
  "/home",
  "/livro/",
  "/book/",
  "/profile",
  "/leagues",
  "/badges",
  "/shelf/",
  "/lists",
  "/list/",
  "/store",
] as const;

export function isSafeCelebrationRoute(pathname: string): boolean {
  return SAFE_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p.endsWith("/") ? p : `${p}/`),
  );
}

export interface BadgeUnlockQueueState {
  /** Lote congelado no momento da abertura — `[]` enquanto não é hora. */
  batch: string[];
  dismiss: () => void;
}

export function useBadgeUnlockQueue(enabled: boolean): BadgeUnlockQueueState {
  const { pathname } = useLocation();
  const [queue, setQueue] = useState<string[]>([]);
  const [blocked, setBlocked] = useState<boolean>(() => isCelebrationBlocked());
  const [batch, setBatch] = useState<string[]>([]);

  useEffect(() => subscribeBadgeQueue(setQueue), []);
  useEffect(() => subscribeCelebrationGate(setBlocked), []);

  const canOpen =
    enabled && queue.length > 0 && !blocked && isSafeCelebrationRoute(pathname);

  useEffect(() => {
    // Já tem lote aberto: não reabre nem recalcula — quem fecha é o `dismiss`.
    if (batch.length > 0) return;
    if (!canOpen) return;

    const t = setTimeout(() => {
      // Releitura da fila no disparo: o timer pode ter atravessado a chegada de
      // mais uma conquista, e o lote deve incluí-la em vez de deixá-la para um
      // segundo cartão logo em seguida.
      setBatch(() => {
        const current = queue;
        return current.length > 0 ? [...current] : [];
      });
    }, SETTLE_MS);

    return () => clearTimeout(t);
  }, [canOpen, pathname, queue, batch.length]);

  return useMemo(
    () => ({
      batch,
      dismiss: () => {
        if (batch.length > 0) dequeueBadges(batch);
        setBatch([]);
      },
    }),
    [batch],
  );
}
